import jsPDF from 'jspdf';
import type { Invoice, Payment, SalesInvoice, DispenseEvent } from './supabase';

export const generateInvoicePDF = (invoice: Invoice) => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.text('MEDICAL INVOICE', 20, 30);
  
  // Invoice details
  doc.setFontSize(12);
  doc.text(`Invoice #: ${invoice.invoice_number}`, 20, 50);
  doc.text(`Issue Date: ${new Date(invoice.issue_date).toLocaleDateString()}`, 20, 60);
  doc.text(`Due Date: ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}`, 20, 70);
  
  // Patient info
  doc.text('Bill To:', 20, 90);
  doc.text(invoice.patient?.name || 'Unknown Patient', 20, 100);
  if (invoice.patient?.email) {
    doc.text(invoice.patient.email, 20, 110);
  }
  if (invoice.patient?.phone) {
    doc.text(invoice.patient.phone, 20, 120);
  }
  
  // Items table header
  let yPos = 140;
  doc.text('Description', 20, yPos);
  doc.text('Qty', 120, yPos);
  doc.text('Price', 140, yPos);
  doc.text('Total', 170, yPos);
  
  // Draw line under header
  doc.line(20, yPos + 5, 190, yPos + 5);
  yPos += 15;
  
  // Items
  if (invoice.items) {
    invoice.items.forEach((item) => {
      doc.text(item.description, 20, yPos);
      doc.text(item.quantity.toString(), 120, yPos);
      doc.text(`$${item.unit_price.toFixed(2)}`, 140, yPos);
      doc.text(`$${item.total.toFixed(2)}`, 170, yPos);
      yPos += 10;
    });
  }
  
  // Totals
  yPos += 10;
  doc.line(120, yPos, 190, yPos);
  yPos += 10;
  
  doc.text(`Subtotal: $${invoice.subtotal.toFixed(2)}`, 120, yPos);
  yPos += 10;
  doc.text(`Tax (${invoice.tax_rate}%): $${invoice.tax_amount.toFixed(2)}`, 120, yPos);
  yPos += 10;
  doc.setFontSize(14);
  doc.text(`Total: $${invoice.total.toFixed(2)}`, 120, yPos);
  
  // Notes
  if (invoice.notes) {
    yPos += 20;
    doc.setFontSize(12);
    doc.text('Notes:', 20, yPos);
    yPos += 10;
    doc.text(invoice.notes, 20, yPos);
  }
  
  return doc;
};

export const printInvoice = (invoice: Invoice) => {
  const doc = generateInvoicePDF(invoice);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export const downloadInvoice = (invoice: Invoice) => {
  const doc = generateInvoicePDF(invoice);
  doc.save(`invoice-${invoice.invoice_number}.pdf`);
};

export const generateReceiptPDF = (payment: Payment, invoice: Invoice) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('Payment Receipt', 20, 30);
  
  doc.setFontSize(12);
  doc.text(`Receipt #: ${payment.reference_number || payment.id}`, 20, 50);
  doc.text(`Date: ${payment.payment_date}`, 20, 60);
  doc.text(`Invoice #: ${invoice.invoice_number}`, 20, 70);
  
  doc.text(`Patient: ${invoice.patient?.name || 'N/A'}`, 20, 90);
  
  doc.text(`Amount Paid: $${payment.amount.toFixed(2)}`, 20, 110);
  doc.text(`Payment Method: ${payment.payment_method}`, 20, 120);
  doc.text(`Reference: ${payment.reference_number || 'N/A'}`, 20, 130);
  
  if (payment.notes) {
    doc.text(`Notes: ${payment.notes}`, 20, 140);
  }
  
  return doc;
};

export const printReceipt = (payment: Payment, invoice: Invoice) => {
  const doc = generateReceiptPDF(payment, invoice);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export const downloadReceipt = (payment: Payment, invoice: Invoice) => {
  const doc = generateReceiptPDF(payment, invoice);
  doc.save(`Receipt_${payment.reference_number || payment.id}_${payment.payment_date}.pdf`);
};

export const generateLabReportPDF = (order: any, patient: any, encounter: any) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('LABORATORY REPORT', 20, 30);
  
  doc.setFontSize(12);
  doc.text(`Report ID: ${order.id}`, 20, 50);
  doc.text(`Order Date: ${new Date(order.createdAt).toLocaleDateString()}`, 20, 60);
  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 20, 70);
  
  doc.text('Patient Information:', 20, 90);
  doc.text(`Name: ${patient.name}`, 20, 100);
  doc.text(`DOB: ${patient.date_of_birth || 'N/A'}`, 20, 110);
  if (patient.phone) {
    doc.text(`Phone: ${patient.phone}`, 20, 120);
  }
  
  doc.text(`Referring Doctor: ${encounter.doctor?.name || 'N/A'}`, 120, 100);
  doc.text(`Specialization: ${encounter.doctor?.specialization || 'N/A'}`, 120, 110);
  
  let yPos = 140;
  doc.setFontSize(14);
  doc.text('Test Results:', 20, yPos);
  yPos += 15;
  
  doc.setFontSize(10);
  doc.text('Test', 20, yPos);
  doc.text('Result', 70, yPos);
  doc.text('Units', 110, yPos);
  doc.text('Reference Range', 140, yPos);
  doc.text('Flag', 180, yPos);
  
  doc.line(20, yPos + 5, 190, yPos + 5);
  yPos += 15;
  
  if (order.results) {
    order.results.forEach((result: any) => {
      doc.text(result.itemName, 20, yPos);
      doc.text(result.resultValue, 70, yPos);
      doc.text(result.units || '', 110, yPos);
      doc.text(result.referenceRange || '', 140, yPos);
      
      if (result.flag === 'H') {
        doc.setTextColor(255, 0, 0);
      } else if (result.flag === 'L') {
        doc.setTextColor(0, 0, 255);
      } else {
        doc.setTextColor(0, 0, 0);
      }
      doc.text(result.flag || 'N', 180, yPos);
      doc.setTextColor(0, 0, 0);
      
      yPos += 10;
      
      if (result.notes) {
        doc.setFontSize(8);
        doc.text(`Note: ${result.notes}`, 25, yPos);
        doc.setFontSize(10);
        yPos += 8;
      }
      
      yPos += 5;
    });
  }
  
  yPos += 20;
  doc.setFontSize(8);
  doc.text('This report is computer generated and does not require signature.', 20, yPos);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, yPos + 10);
  
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
  
  return doc;
};

export const printLabReport = (order: any, patient: any, encounter: any) => {
  const doc = generateLabReportPDF(order, patient, encounter);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export const downloadLabReport = (order: any, patient: any, encounter: any) => {
  const doc = generateLabReportPDF(order, patient, encounter);
  doc.save(`lab-report-${order.id}.pdf`);
};

export const generatePOSInvoicePDF = (invoice: SalesInvoice) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('PHARMACY SALES RECEIPT', 20, 30);
  
  doc.setFontSize(12);
  doc.text(`Receipt #: ${invoice.invoice_number}`, 20, 50);
  doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString()}`, 20, 60);
  
  if (invoice.customer_name) {
    doc.text('Customer:', 20, 80);
    doc.text(invoice.customer_name, 20, 90);
    if (invoice.customer_phone) {
      doc.text(invoice.customer_phone, 20, 100);
    }
  }
  
  let yPos = 120;
  doc.text('Item', 20, yPos);
  doc.text('Qty', 120, yPos);
  doc.text('Price', 140, yPos);
  doc.text('Total', 170, yPos);
  
  doc.line(20, yPos + 5, 190, yPos + 5);
  yPos += 15;
  
  if (invoice.items) {
    invoice.items.forEach((item) => {
      doc.text(item.product?.name || 'Unknown Product', 20, yPos);
      doc.text(item.quantity.toString(), 120, yPos);
      doc.text(`$${item.unit_price.toFixed(2)}`, 140, yPos);
      doc.text(`$${item.total.toFixed(2)}`, 170, yPos);
      yPos += 10;
    });
  }
  
  yPos += 10;
  doc.line(120, yPos, 190, yPos);
  yPos += 10;
  
  doc.text(`Subtotal: $${invoice.subtotal.toFixed(2)}`, 120, yPos);
  yPos += 10;
  if (invoice.discount_amount > 0) {
    doc.text(`Discount: -$${invoice.discount_amount.toFixed(2)}`, 120, yPos);
    yPos += 10;
  }
  doc.text(`Tax: $${invoice.tax_amount.toFixed(2)}`, 120, yPos);
  yPos += 10;
  doc.setFontSize(14);
  doc.text(`Total: $${invoice.total.toFixed(2)}`, 120, yPos);
  
  return doc;
};

export const generateDispenseLabelPDF = (dispenseEvent: DispenseEvent) => {
  const doc = new jsPDF('p', 'mm', [80, 60]);
  
  doc.setFontSize(10);
  doc.text('PHARMACY DISPENSE', 5, 10);
  doc.text(`Patient: ${dispenseEvent.patient?.name || 'Unknown'}`, 5, 20);
  doc.text(`Product: ${dispenseEvent.product?.name || 'Unknown'}`, 5, 30);
  doc.text(`Qty: ${dispenseEvent.quantity}`, 5, 40);
  doc.text(`Date: ${new Date(dispenseEvent.created_at).toLocaleDateString()}`, 5, 50);
  
  return doc;
};

export const printPOSInvoice = (invoice: SalesInvoice) => {
  const doc = generatePOSInvoicePDF(invoice);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export const downloadPOSInvoice = (invoice: SalesInvoice) => {
  const doc = generatePOSInvoicePDF(invoice);
  doc.save(`pos-invoice-${invoice.invoice_number}.pdf`);
};

export const generateDischargeSummaryPDF = (summaryData: {
  summaryNo: string;
  patient: { name: string; phone?: string; date_of_birth?: string };
  admission: { admission_date: string; discharge_date: string };
  doctor: { name: string; specialization?: string };
  admissionDiagnosis?: string;
  finalDiagnosis?: string;
  treatmentSummary?: string;
  proceduresPerformed?: string;
  medicationsPrescribed?: string;
  followUpInstructions?: string;
  dietRestrictions?: string;
  activityRestrictions?: string;
  followUpDate?: string;
}) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('Discharge Summary', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Summary No: ${summaryData.summaryNo}`, 20, 40);
  doc.text(`Patient: ${summaryData.patient.name}`, 20, 50);
  if (summaryData.patient.date_of_birth) {
    doc.text(`DOB: ${new Date(summaryData.patient.date_of_birth).toLocaleDateString()}`, 20, 60);
  }
  doc.text(`Admission: ${new Date(summaryData.admission.admission_date).toLocaleDateString()}`, 20, 70);
  doc.text(`Discharge: ${new Date(summaryData.admission.discharge_date).toLocaleDateString()}`, 20, 80);
  doc.text(`Doctor: ${summaryData.doctor.name}`, 20, 90);
  
  let yPos = 110;
  
  const addSection = (title: string, content?: string) => {
    if (!content) return;
    doc.setFontSize(14);
    doc.text(title, 20, yPos);
    yPos += 10;
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(content, 170);
    doc.text(lines, 20, yPos);
    yPos += lines.length * 6 + 5;
  };
  
  addSection('Admission Diagnosis', summaryData.admissionDiagnosis);
  addSection('Final Diagnosis', summaryData.finalDiagnosis);
  addSection('Treatment Summary', summaryData.treatmentSummary);
  addSection('Procedures Performed', summaryData.proceduresPerformed);
  addSection('Medications Prescribed', summaryData.medicationsPrescribed);
  addSection('Follow-up Instructions', summaryData.followUpInstructions);
  addSection('Diet Restrictions', summaryData.dietRestrictions);
  addSection('Activity Restrictions', summaryData.activityRestrictions);
  
  if (summaryData.followUpDate) {
    doc.setFontSize(12);
    doc.text(`Follow-up Date: ${new Date(summaryData.followUpDate).toLocaleDateString()}`, 20, yPos);
  }
  
  return doc;
};

export const downloadDischargeSummary = (summaryData: any) => {
  const doc = generateDischargeSummaryPDF(summaryData);
  doc.save(`discharge-summary-${summaryData.summaryNo}.pdf`);
};

export const printDispenseLabel = (dispenseEvent: DispenseEvent) => {
  const doc = generateDispenseLabelPDF(dispenseEvent);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};
export const generateBirthCertificatePDF = (certificateData: {
  certificate_no: string;
  patient_name: string;
  date_of_birth: string;
  time_of_birth: string;
  weight: string;
  apgar_score?: string;
  mother_name?: string;
  father_name?: string;
  hospital_name: string;
  issued_date: string;
}) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('BIRTH CERTIFICATE', 105, 30, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Certificate No: ${certificateData.certificate_no}`, 20, 50);
  
  doc.setFontSize(14);
  doc.text('Child Information', 20, 70);
  doc.setFontSize(12);
  doc.text(`Name: ${certificateData.patient_name}`, 30, 85);
  doc.text(`Date of Birth: ${new Date(certificateData.date_of_birth).toLocaleDateString()}`, 30, 95);
  doc.text(`Time of Birth: ${certificateData.time_of_birth}`, 30, 105);
  doc.text(`Weight at Birth: ${certificateData.weight}`, 30, 115);
  if (certificateData.apgar_score) {
    doc.text(`APGAR Score: ${certificateData.apgar_score}`, 30, 125);
  }
  
  doc.setFontSize(14);
  doc.text('Parent Information', 20, 145);
  doc.setFontSize(12);
  if (certificateData.mother_name) {
    doc.text(`Mother's Name: ${certificateData.mother_name}`, 30, 160);
  }
  if (certificateData.father_name) {
    doc.text(`Father's Name: ${certificateData.father_name}`, 30, 170);
  }
  
  doc.setFontSize(14);
  doc.text('Hospital Information', 20, 190);
  doc.setFontSize(12);
  doc.text(`Hospital: ${certificateData.hospital_name}`, 30, 205);
  doc.text(`Issue Date: ${new Date(certificateData.issued_date).toLocaleDateString()}`, 30, 215);
  
  doc.setFontSize(10);
  doc.text('This is a computer-generated certificate.', 20, 260);
  
  return doc;
};

export const generateDeathCertificatePDF = (certificateData: {
  certificate_no: string;
  patient_name: string;
  date_of_death: string;
  time_of_death: string;
  cause_of_death: string;
  age?: string;
  hospital_name: string;
  issued_date: string;
}) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('DEATH CERTIFICATE', 105, 30, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Certificate No: ${certificateData.certificate_no}`, 20, 50);
  
  doc.setFontSize(14);
  doc.text('Deceased Information', 20, 70);
  doc.setFontSize(12);
  doc.text(`Name: ${certificateData.patient_name}`, 30, 85);
  if (certificateData.age) {
    doc.text(`Age: ${certificateData.age}`, 30, 95);
  }
  doc.text(`Date of Death: ${new Date(certificateData.date_of_death).toLocaleDateString()}`, 30, 105);
  doc.text(`Time of Death: ${certificateData.time_of_death}`, 30, 115);
  
  doc.setFontSize(14);
  doc.text('Cause of Death', 20, 135);
  doc.setFontSize(12);
  const causeLines = doc.splitTextToSize(certificateData.cause_of_death, 160);
  doc.text(causeLines, 30, 150);
  
  const yPos = 150 + causeLines.length * 6 + 20;
  doc.setFontSize(14);
  doc.text('Hospital Information', 20, yPos);
  doc.setFontSize(12);
  doc.text(`Hospital: ${certificateData.hospital_name}`, 30, yPos + 15);
  doc.text(`Issue Date: ${new Date(certificateData.issued_date).toLocaleDateString()}`, 30, yPos + 25);
  
  doc.setFontSize(10);
  doc.text('This is a computer-generated certificate.', 20, 260);
  
  return doc;
};

export const generateSurgeryNotePDF = (surgeryData: {
  surgery_no: string;
  patient_name: string;
  admission_no: string;
  surgeon_name: string;
  procedure_name: string;
  scheduled_at: string;
  started_at?: string;
  completed_at?: string;
  ot_room?: string;
  anesthesia_type?: string;
  findings?: string;
  complications?: string;
  post_op_plan?: string;
}) => {
  const doc = new jsPDF();
  
  doc.setFontSize(20);
  doc.text('SURGERY / OT NOTE', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.text(`Surgery No: ${surgeryData.surgery_no}`, 20, 40);
  doc.text(`Patient: ${surgeryData.patient_name}`, 20, 50);
  doc.text(`Admission No: ${surgeryData.admission_no}`, 20, 60);
  
  doc.setFontSize(14);
  doc.text('Procedure Details', 20, 80);
  doc.setFontSize(12);
  doc.text(`Surgeon: ${surgeryData.surgeon_name}`, 30, 95);
  doc.text(`Procedure: ${surgeryData.procedure_name}`, 30, 105);
  doc.text(`Scheduled: ${new Date(surgeryData.scheduled_at).toLocaleString()}`, 30, 115);
  if (surgeryData.started_at) {
    doc.text(`Started: ${new Date(surgeryData.started_at).toLocaleString()}`, 30, 125);
  }
  if (surgeryData.completed_at) {
    doc.text(`Completed: ${new Date(surgeryData.completed_at).toLocaleString()}`, 30, 135);
  }
  if (surgeryData.ot_room) {
    doc.text(`OT Room: ${surgeryData.ot_room}`, 30, 145);
  }
  if (surgeryData.anesthesia_type) {
    doc.text(`Anesthesia: ${surgeryData.anesthesia_type}`, 30, 155);
  }
  
  let yPos = 175;
  
  if (surgeryData.findings) {
    doc.setFontSize(14);
    doc.text('Operative Findings', 20, yPos);
    doc.setFontSize(10);
    const findingsLines = doc.splitTextToSize(surgeryData.findings, 170);
    doc.text(findingsLines, 20, yPos + 10);
    yPos += findingsLines.length * 6 + 20;
  }
  
  if (surgeryData.complications) {
    doc.setFontSize(14);
    doc.text('Complications', 20, yPos);
    doc.setFontSize(10);
    const compLines = doc.splitTextToSize(surgeryData.complications, 170);
    doc.text(compLines, 20, yPos + 10);
    yPos += compLines.length * 6 + 20;
  }
  
  if (surgeryData.post_op_plan) {
    doc.setFontSize(14);
    doc.text('Post-operative Plan', 20, yPos);
    doc.setFontSize(10);
    const planLines = doc.splitTextToSize(surgeryData.post_op_plan, 170);
    doc.text(planLines, 20, yPos + 10);
  }
  
  return doc;
};

export const downloadBirthCertificate = (certificateData: any) => {
  const doc = generateBirthCertificatePDF(certificateData);
  doc.save(`birth-certificate-${certificateData.certificate_no}.pdf`);
};

export const downloadDeathCertificate = (certificateData: any) => {
  const doc = generateDeathCertificatePDF(certificateData);
  doc.save(`death-certificate-${certificateData.certificate_no}.pdf`);
};

export const downloadSurgeryNote = (surgeryData: any) => {
  const doc = generateSurgeryNotePDF(surgeryData);
  doc.save(`surgery-note-${surgeryData.surgery_no}.pdf`);
};
